import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Icon } from "@iconify/react";
import { FiExternalLink } from "react-icons/fi";
import AbLogo from "../../../assets/logo192.png";
import ImgOptimized from "../../../components/ImgOptimized";
import watermark from "../../../assets/water.png";
import { BRANDS_LIST } from "../../../utils/helpers";
import FullPageImageSlider from "../../../components/FullPageImageSlider";

const MaterialDetailSidebar = ({ item, onClose }) => {
  const [activeImg, setActiveImg] = useState(0);
  const [visible, setVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const sliderRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const material = item?.material || item;
  if (!material) return null;

  const brandObj = BRANDS_LIST?.find((b) => b?._id == material?.brand);
  const brandName = brandObj?.label || material?.brand || "-";

  const details = [
    { label: "Product Code", value: material?.productCode },
    { label: "Material Type", value: material?.materialType },
    { label: "Brand", value: brandName },
    { label: "Category", value: material?.otherDetails?.category },
    { label: "Sub Category", value: material?.otherDetails?.subCategory },
    {
      label: "Size",
      value: Array.isArray(material?.otherDetails?.size)
        ? material.otherDetails.size.join(", ")
        : material?.otherDetails?.size,
    },
  ].filter((d) => d.value);

  const images = material?.images || [];
  const imageUrls = images.map(
    (img) => `${process.env.REACT_APP_BACK_URL}/api/minio/${img}`,
  );

  const slickSettings = {
    dots: images.length > 1,
    infinite: images.length > 1,
    autoplay: images.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (_, next) => setActiveImg(next),
    appendDots: (dots) => (
      <div style={{ bottom: 6 }}>
        <ul className="flex justify-center gap-1">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <button
        className={`w-1.5 h-1.5 rounded-full transition-colors ${
          i === activeImg ? "bg-teal-600" : "bg-gray-300"
        }`}
      />
    ),
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[999] flex justify-end">
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            visible ? "bg-opacity-40" : "bg-opacity-0"
          }`}
          onClick={handleClose}
        />
        <div
          className={`relative w-[420px] max-w-full h-full bg-white flex flex-col shadow-2xl transition-transform duration-300 ${
            visible ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Header */}
          <div className="bg-[#06565E] text-white px-4 py-3 flex items-start justify-between gap-2 shrink-0">
            <div className="min-w-0">
              <p className="text-xs text-teal-200 font-medium uppercase tracking-wide">
                {material?.materialType}
              </p>
              <h2 className="font-bold text-lg leading-tight">
                {material?.materialName}
              </h2>
              {material?.productCode && (
                <p className="text-teal-200 text-xs mt-0.5">
                  #{material.productCode}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="shrink-0 p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors mt-0.5"
            >
              <Icon icon="mdi:close" width={22} />
            </button>
          </div>

          {/* Slick Carousel */}
          <div className="bg-gray-50 shrink-0 relative" style={{ height: 240 }}>
            {images.length > 0 ? (
              <>
                <Slider ref={sliderRef} {...slickSettings}>
                  {images.map((img, i) => (
                    <div
                      key={i}
                      className="outline-none "
                      style={{ height: 240 }}
                      onClick={() => setLightboxOpen(true)}
                    >
                      <ImgOptimized
                        src={`${process.env.REACT_APP_BACK_URL}/api/minio/${img}`}
                        className="w-full object-contain p-2"
                        style={{ height: 240 }}
                        alt={`${material?.materialName} ${i + 1}`}
                      />
                    </div>
                  ))}
                </Slider>
                {images.length > 1 && (
                  <>
                    <button
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-90 rounded-full p-1 shadow-md hover:bg-opacity-100 transition"
                      onClick={() => sliderRef.current?.slickPrev()}
                    >
                      <Icon
                        icon="mdi:chevron-left"
                        width={20}
                        color="#06565E"
                      />
                    </button>
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-90 rounded-full p-1 shadow-md hover:bg-opacity-100 transition"
                      onClick={() => sliderRef.current?.slickNext()}
                    >
                      <Icon
                        icon="mdi:chevron-right"
                        width={20}
                        color="#06565E"
                      />
                    </button>
                  </>
                )}
                {/* <div className="absolute bottom-2 right-2 z-10 bg-black bg-opacity-40 rounded-full p-1 text-white pointer-events-none">
                <Icon icon="mdi:fullscreen" width={16} />
              </div> */}
              </>
            ) : (
              <div className="flex items-center justify-center w-full h-full text-gray-300">
                <Icon icon="mdi:image-off-outline" width={56} />
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100 overflow-x-auto shrink-0">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveImg(i);
                    sliderRef.current?.slickGoTo(i);
                  }}
                  className={`w-14 h-14 shrink-0 rounded-lg border-2 overflow-hidden bg-white transition-colors ${
                    activeImg === i
                      ? "border-teal-500"
                      : "border-gray-200 hover:border-teal-300"
                  }`}
                >
                  <ImgOptimized
                    src={`${process.env.REACT_APP_BACK_URL}/api/minio/${img}`}
                    className="w-full h-full object-contain p-0.5"
                    alt={`${material?.materialName} ${i + 1}`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Details */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {details.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {details.map(({ label, value }) => (
                  <div
                    key={label}
                    className="bg-gray-50 rounded-xl p-3 border border-gray-100"
                  >
                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">
                      {label}
                    </p>
                    <p className="text-sm font-semibold text-gray-800 capitalize leading-snug">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {material?.areas?.length > 0 && (
              <div>
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-2">
                  Available Areas
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {material.areas.map((area) => (
                    <span
                      key={area}
                      className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {material?.otherDetails?.productUrl && (
              <a
                href={material.otherDetails.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#06565E] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors"
              >
                <FiExternalLink size={15} />
                View Product
              </a>
            )}
          </div>
        </div>
      </div>
      {/* {lightboxOpen && imageUrls.length > 0 && (
      <FullPageImageSlider
        images={imageUrls}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        initialIndex={activeImg}
      />
    )} */}
    </>,
    document.body,
  );
};

const QuantityTable = ({ values, heading, isPdf, isViewByZone = false }) => {
  // console.log("QuantityTable", { heading, values, isPdf });
  const [selectedItem, setSelectedItem] = useState(null);

  // Check if any object has a size key
  const hasSize =
    values && Array.isArray(values) && values.some((item) => item?.size);

  // Check if any object has a brand key
  const hasBrand = isViewByZone
    ? values && Array.isArray(values) && values.some((item) => item?.brand)
    : true;

  // Calculate colspan for empty state
  const colSpan = (hasSize ? 1 : 0) + (hasBrand ? 1 : 0) + 3;

  return (
    <div className="p-2 w-full h-full">
      <div
        className={`bg-white text-black w-full ${isPdf ? "min-h-[100svh]" : ""} h-full flex flex-col items-center justify-start p-4 relative  gap-6 `}
      >
        <div className="w-full flex justify-center">
          <h2
            className={` ${
              isPdf ? "text-2xl" : "text-3xl"
            } font-bold text-[#06565E] italic playfair capitalize text-center`}
          >
            {heading}
          </h2>
        </div>

        <ImgOptimized
          src={watermark}
          className="absolute top-1/2 opacity-30 -translate-x-1/2 pointer-events-none -translate-y-1/2  left-1/2 w-4/5 z-[50] "
          alt=""
        />
        <img
          src={AbLogo}
          alt="AirBrick Logo"
          className="max-w-full h-12 absolute top-4 left-4 "
        />
        <div className="w-full max-w-2xl   mx-auto">
          {/* <h2 className='text-2xl font-bold mb-6 text-center text-teal-600'>{heading}</h2> */}

          <div className="overflow-x-auto shadow-lg rounded-lg">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-teal-500 text-white">
                  {hasBrand && isViewByZone && (
                    <th className="border border-gray-300 px-6 py-2 text-left font-semibold">
                      Brand
                    </th>
                  )}
                  <th className="border border-gray-300 px-6 py-2 text-left font-semibold">
                    SKU
                  </th>
                  {hasBrand && !isViewByZone && (
                    <th className="border border-gray-300 px-6 py-2 text-left font-semibold">
                      Zone
                    </th>
                  )}
                  {hasSize && (
                    <th className="border border-gray-300 px-6 py-2 text-left font-semibold">
                      Size
                    </th>
                  )}
                  <th className="border border-gray-300 px-6 py-2 text-center font-semibold">
                    Quantity
                  </th>
                  <th className="border border-gray-300 px-6 py-2 text-center font-semibold">
                    Unit
                  </th>
                </tr>
              </thead>
              <tbody>
                {values && Array.isArray(values) && values.length > 0 ? (
                  values.map((item, idx) => (
                    <tr
                      key={idx}
                      onClick={() => item?.material && setSelectedItem(item)}
                      className={`hover:bg-gray-100 transition-colors ${item?.material ? "cursor-pointer" : ""} ${
                        idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      {hasBrand && isViewByZone && (
                        <td className="border border-gray-300 px-6 py-2 text-gray-800 capitalize">
                          {item?.brand || "-"}
                        </td>
                      )}
                      <td className="border border-gray-300 px-6 py-2 text-gray-800 font-mono text-sm">
                        {item?.sku}
                      </td>
                      {hasBrand && !isViewByZone && (
                        <td className="border border-gray-300 px-6 py-2 text-gray-800 capitalize">
                          {item?.heading || "-"}
                        </td>
                      )}
                      {hasSize && (
                        <td className="border border-gray-300 px-6 py-2 text-gray-800 capitalize">
                          {item?.size && Array.isArray(item?.size)
                            ? item.size.join(", ")
                            : item?.size || "-"}
                        </td>
                      )}
                      <td className="border border-gray-300 px-6 py-2 text-center font-semibold text-gray-800">
                        {item?.quantity}
                      </td>
                      <td className="border border-gray-300 px-6 py-2 text-center font-semibold text-gray-800">
                        {item?.uom}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="border border-gray-300 px-6 py-8 text-center text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {values && Array.isArray(values) && values.length > 0 && (
            <div className="mt-2 flex justify-end gap-8 text-gray-700 font-semibold">
              <div>
                Total Items:{" "}
                <span className="text-teal-600">{values.length}</span>
              </div>
              <div>
                Total Quantity:
                <span className="text-teal-600 ml-2">
                  {values.reduce((sum, item) => sum + (item?.quantity || 0), 0)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedItem && (
        <MaterialDetailSidebar
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default QuantityTable;
